import {
  call,
  CallEffect,
  put,
  PutEffect,
  takeLatest,
  select,
  SelectEffect,
} from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import {
  sendForgetPasswordEmailSuccess,
  setForgetPassword,
  setForgetPasswordEmail,
  setForgetPasswordError,
  setNewPasswordSuccess,
} from './forgetPasswordSlice';
import { IAuthResponse, resetPassword, sendForgetPasswordEmail } from '../../service/api/authApi';
import { toastStyles } from '../../utils/utils';
import i18n from '../../i18n';

function* watchForgetPasswordEmailRequest({
  payload: email,
}: PayloadAction<string>): Generator<CallEffect | PutEffect, void, IAuthResponse> {
  try {
    const response = yield call(sendForgetPasswordEmail, email);
    const data = yield response.json();
    if (data.statusCode === 200) {
      yield put(sendForgetPasswordEmailSuccess());
    } else {
      if (data.statusCode === 400) {
        toast.error(i18n.t('forgetPassword.toastMessage.invalidUsername'), toastStyles.error);
        yield put(setForgetPasswordError(i18n.t('forgetPassword.toastMessage.invalidUsername')));
        return;
      }

      throw new Error(data.message);
    }
  } catch (err) {
    toast.error(i18n.t('forgetPassword.toastMessage.failedToSend'), toastStyles.error);
    if (err instanceof Error) yield put(setForgetPasswordError(err.toString()));
  }
}

function* watchForgetPasswordNewPasswordRequest({
  payload: password,
}: PayloadAction<string>): Generator<
  CallEffect | PutEffect | SelectEffect,
  void,
  IAuthResponse | string
> {
  try {
    const email = yield select((state) => state.forgetPassword.email);
    const verificationCode = yield select((state) => state.forgetPassword.code);

    const response = yield call(
      resetPassword,
      email as string,
      verificationCode as string,
      password as string
    );
    const data = yield (response as IAuthResponse).json();
    const { statusCode, message } = data as IAuthResponse;

    if (statusCode === 200) {
      yield put(setNewPasswordSuccess());
    } else {
      if (statusCode === 400) {
        toast.error(
          i18n.t('forgetPassword.toastMessage.invalidVerificationCode'),
          toastStyles.error
        );
        yield put(
          setForgetPasswordError(i18n.t('forgetPassword.toastMessage.invalidVerificationCode'))
        );
        return;
      }

      throw new Error(message);
    }
  } catch (err) {
    toast.error(i18n.t('forgetPassword.toastMessage.failedToReset'), toastStyles.error);
    if (err instanceof Error) yield put(setForgetPasswordError(err.toString()));
  }
}

export function* watchForgetPasswordSagas() {
  yield takeLatest(setForgetPasswordEmail, watchForgetPasswordEmailRequest);
  yield takeLatest(setForgetPassword, watchForgetPasswordNewPasswordRequest);
}

const forgetPasswordSagas = watchForgetPasswordSagas;

export default forgetPasswordSagas;
